export interface ITextTrackItem {
  id: string
  label?: string
  language?: string
  kind?: string
  isCaptions?: boolean
}

const TEXT_TRACK_STORAGE_KEY = 'pureya__text-track'
const PLUG_ID = 'plug'

/**
 * Default subtitle track representing "Off" state
 */
export const DEFAULT_SUBTITLE_TRACK: ITextTrackItem = {
  id: `${PLUG_ID}-default`,
  language: 'Off',
  label: 'Off',
  kind: 'subtitles',
  isCaptions: false,
}

/**
 * Get base language from full language code (e.g., 'en-US' -> 'en')
 */
const getBaseLanguage = (language: string): string => {
  return language.split('-')[0].toLowerCase()
}

/**
 * Check if two languages match (exact or base language match)
 */
const isLanguageMatch = ({
  track,
  searchedLanguage,
  exactMatch = true,
}: {
  track: ITextTrackItem
  searchedLanguage?: string | null
  exactMatch?: boolean
}): boolean => {
  if (!searchedLanguage || !track.language) return false

  const searchedLower = searchedLanguage.toLowerCase()
  const trackLangLower = track.language.toLowerCase()

  if (exactMatch) {
    return track.language === searchedLanguage
  } else {
    const searchedBase = getBaseLanguage(searchedLower)
    const trackBase = getBaseLanguage(trackLangLower)

    return trackBase.includes(searchedBase) || searchedBase.includes(trackBase)
  }
}

/**
 * Find exact track match
 */
const findExactTrack = ({
  textTracks,
  textTrack,
}: {
  textTracks: ITextTrackItem[]
  textTrack?: ITextTrackItem | null
}): ITextTrackItem | undefined => {
  return textTracks.find((track) => {
    return (
      isLanguageMatch({
        track,
        searchedLanguage: textTrack?.language,
        exactMatch: true,
      }) &&
      textTrack?.id === track.id &&
      textTrack?.isCaptions === track.isCaptions
    )
  })
}

/**
 * Find track by base language match
 */
const findBaseLanguageTrack = ({
  textTracks,
  searchedLanguage,
  preferCaptions,
}: {
  textTracks: ITextTrackItem[]
  searchedLanguage?: string | null
  preferCaptions: boolean
}): ITextTrackItem | undefined => {
  return textTracks.find((track) => {
    const isSameLang = isLanguageMatch({
      track,
      searchedLanguage,
      exactMatch: false,
    })
    if (!isSameLang) return false

    return preferCaptions ? track.isCaptions : !track.isCaptions
  })
}

/**
 * Get stored text track from localStorage
 */
export const getStoredTextTrack = (): ITextTrackItem | null => {
  try {
    const textTrack = localStorage.getItem(TEXT_TRACK_STORAGE_KEY)
    return textTrack ? JSON.parse(textTrack) : null
  } catch (ignore) {
    return null
  }
}

/**
 * Save text track to localStorage
 */
export const saveTextTrack = (textTrack: ITextTrackItem): void => {
  try {
    localStorage.setItem(TEXT_TRACK_STORAGE_KEY, JSON.stringify(textTrack))
  } catch (ignore) {
    // localStorage might not be available
  }
}

/**
 * Save "Off" track to localStorage
 * Used when user manually selects "Off"
 */
export const saveOffTextTrack = (): void => {
  try {
    localStorage.setItem(
      TEXT_TRACK_STORAGE_KEY,
      JSON.stringify(DEFAULT_SUBTITLE_TRACK)
    )
  } catch (ignore) {
    // localStorage might not be available
  }
}

/**
 * Get stored selected text track with priority matching
 */
const getStoredSelectedTextTrack = (
  textTracks: ITextTrackItem[]
): ITextTrackItem | undefined => {
  const storedTextTrack = getStoredTextTrack()
  if (!storedTextTrack) return undefined

  // If stored track is the "Off" default track, return undefined to skip selection
  if (storedTextTrack.id === DEFAULT_SUBTITLE_TRACK.id) {
    return undefined
  }

  // Priority order:
  // 1. Exact match
  // 2. Base language match with captions
  // 3. Base language match with subtitles

  return (
    findExactTrack({
      textTracks,
      textTrack: storedTextTrack,
    }) ||
    findBaseLanguageTrack({
      textTracks,
      searchedLanguage: storedTextTrack.language,
      preferCaptions: true,
    }) ||
    findBaseLanguageTrack({
      textTracks,
      searchedLanguage: storedTextTrack.language,
      preferCaptions: false,
    })
  )
}

/**
 * Get app locale selected text track
 */
const getAppLocaleSelectedTextTrack = (
  textTracks: ITextTrackItem[],
  appLocale?: string | null
): ITextTrackItem | undefined => {
  if (!appLocale) return undefined

  // Priority order:
  // 1. Base language match with captions
  // 2. Base language match with subtitles
  return (
    findBaseLanguageTrack({
      textTracks,
      searchedLanguage: appLocale,
      preferCaptions: true,
    }) ||
    findBaseLanguageTrack({
      textTracks,
      searchedLanguage: appLocale,
      preferCaptions: false,
    })
  )
}

/**
 * Set preferable text track based on stored preferences and app locale
 * Priority order:
 * 1. Stored exact match
 * 2. Stored Base language match with captions
 * 3. Stored Base language match with subtitles
 * 4. App locale Base language match with captions
 * 5. App locale Base language match with subtitles
 * 6. Fallback to first available
 */
export const setPreferableTextTrack = ({
  textTracks,
  appLocale,
  onTrackSelected,
}: {
  textTracks: ITextTrackItem[]
  appLocale?: string | null
  onTrackSelected: (track: ITextTrackItem) => void
}): void => {
  if (textTracks.length === 0) return

  const firstTextTrack = textTracks[0]

  // Try stored selected track first
  const storedSelectedTrack = getStoredSelectedTextTrack(textTracks)

  if (storedSelectedTrack) {
    onTrackSelected(storedSelectedTrack)
    return
  }

  // Try app locale selected track
  const appLocaleSelectedTrack = getAppLocaleSelectedTextTrack(
    textTracks,
    appLocale
  )
  if (appLocaleSelectedTrack) {
    onTrackSelected(appLocaleSelectedTrack)
    return
  }

  // Fallback to first available track
  onTrackSelected(firstTextTrack)
}

/**
 * Convert VideoJS TextTrack to ITextTrackItem
 */
export const convertTextTrackToItem = (track: TextTrack): ITextTrackItem => {
  return {
    id: track.id || track.language || '',
    label: track.label,
    language: track.language,
    kind: track.kind,
    isCaptions: track.kind === 'captions',
  }
}

/**
 * Get all available text tracks as ITextTrackItem array
 */
export const getAvailableTextTracks = (player: any): ITextTrackItem[] => {
  const tracks = player.textTracks()
  if (!tracks || tracks.length === 0) return []

  const textTracks: ITextTrackItem[] = []
  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i]
    if (track.kind === 'captions' || track.kind === 'subtitles') {
      textTracks.push(convertTextTrackToItem(track))
    }
  }
  return textTracks
}
